import { Autocomplete, Box, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { AxiosResponse } from 'axios';
import React, {
  HTMLAttributes,
  JSXElementConstructor,
  SyntheticEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  EndpointType,
  getBuildingsAction,
  getBuildingsRequest,
} from '../../redux/actions/getBuildings';
import { Building, Store } from '../../types';
import { Item } from './BuildingsAutocompleteField.styles';
import { Props } from './BuildingsAutocompleteField.types';

const NoBuildingWrapper: React.FC<{ addNewBuildingHandler: () => void }> = ({
  addNewBuildingHandler,
}) => (
  <Item customColor onClick={addNewBuildingHandler}>
    <Typography variant="subtitle1">Don't see your building?</Typography>
    <Typography variant="caption">Add your building to our database</Typography>
  </Item>
);

export const BuildingsAutocompleteSelectField: React.FC<Props> = ({
  label,
  InputProps,
  value: _value,
  defaultValue,
  onChange: _onChange,
  onAddNewBuilding: _onAddNewBuilding,
  ...props
}) => {
  const dispatch = useDispatch();
  const buildings = useSelector((state: Store) => state.buildings.list);
  const [value, setValue] = useState<Building | null>(defaultValue ?? null);
  const [inputText, setInputText] = useState<string>('');
  const getBuildingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (_value) {
      const result = buildings.find(
        (building) => building.id === Number(_value),
      );
      if (result) {
        setValue(result);
      }
    }
    // eslint-disable-next-line
  }, [_value]);

  const getBuildings = useCallback(
    async (value: string, type: EndpointType) => {
      try {
        dispatch(getBuildingsAction.request());
        const result: AxiosResponse<Building[]> = await getBuildingsRequest(
          value,
          type,
        );

        dispatch(getBuildingsAction.success(result.data));
      } catch (error) {
        dispatch(getBuildingsAction.failure(error as Error));
      }
    },
    [dispatch],
  );

  const onInputChange = useCallback(
    (_: SyntheticEvent, data: string) => {
      if (getBuildingTimeoutRef.current) {
        clearTimeout(getBuildingTimeoutRef.current);
      }
      if (data.length > 1) {
        getBuildingTimeoutRef.current = setTimeout(() => {
          void getBuildings(data, EndpointType.all);
          setInputText(data);
        }, 300);
      } else {
        // NoOptionsText lower part shouldn't be shown
        dispatch(getBuildingsAction.success([]));
        setInputText('');
      }
    },
    [getBuildings, dispatch],
  );

  const onChange = useCallback(
    (_: SyntheticEvent, data: Building | null) => {
      setValue(data);
      _onChange?.(data);
    },
    [_onChange],
  );

  const onAddNewBuilding = useCallback(() => {
    _onAddNewBuilding?.();
  }, [_onAddNewBuilding]);

  const getListBoxComponent = ():
    | JSXElementConstructor<HTMLAttributes<HTMLElement>>
    | undefined =>
    forwardRef((props, ref) => {
      const { children, ...other } = props;
      return (
        <Box component="ul" ref={ref} {...other}>
          {children}
          <NoBuildingWrapper addNewBuildingHandler={onAddNewBuilding} />
        </Box>
      );
    });

  const getNoOptionsNode = () => {
    return (
      <Box component="ul" sx={{ pl: 0, m: '-14px -16px' }}>
        <Item disabled>Start typing to find your building</Item>
        {inputText !== '' && (
          <NoBuildingWrapper addNewBuildingHandler={onAddNewBuilding} />
        )}
      </Box>
    );
  };

  return (
    <Autocomplete
      {...props}
      data-testid={props.testId}
      options={buildings}
      filterOptions={(x) => x}
      renderOption={(props, option) => (
        <Item {...props} key={option.id}>
          <Typography variant="subtitle1">{option.name}</Typography>
          <div>
            <Typography variant="caption">
              {[option.street, option.city, option.state, option.zipCode]
                .filter((v) => v)
                .map((v) => v.trim())
                .join(', ')}
            </Typography>
          </div>
        </Item>
      )}
      getOptionLabel={(option) =>
        [option.street, option.city, option.state, option.zipCode].join(', ')
      }
      onChange={onChange}
      onInputChange={onInputChange}
      value={value}
      ListboxComponent={getListBoxComponent()}
      noOptionsText={getNoOptionsNode()}
      renderInput={(params) => (
        <TextField {...params} label={label} {...InputProps} />
      )}
    />
  );
};
